import { Avatar, Box, Button, Chip, Divider, Grid, Paper, Skeleton, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { impersonationAuditPath, subscribersPath } from '../../logic/paths'
import SharedButton from '../../shared/components/Button'
import InfoRow from '../../shared/components/InfoRow'
import StatCard from '../../shared/components/StatCard'
import SharedTabs from '../../shared/components/Tabs'
import { useThemeMode, useIsSuperAdmin } from '../../shared/hooks'
import Invoice from './invoice'
import AiUsage from './aiUsage'
import ESignature from './eSignature'
import TradesmanSwitchCard from './tradesmanSwitch'
import MetricsExclusionCard from './metricsExclusion'
import LogInAsDialog from './components/LogInAsDialog'
import { getImpersonationMembers } from '../../logic/apis/admin'
import { logInAsVisibleAfterProbe } from '../../logic/interfaces/adminImpersonation'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined'
import UpgradeOutlinedIcon from '@mui/icons-material/UpgradeOutlined'
import RepeatOutlinedIcon from '@mui/icons-material/RepeatOutlined'
import FiberManualRecordOutlinedIcon from '@mui/icons-material/FiberManualRecordOutlined'
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined'
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined'
import CancelScheduleSendOutlinedIcon from '@mui/icons-material/CancelScheduleSendOutlined'
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import { Company } from '../../logic/apis/admin'
import {
  useDeleteCompanyMutation,
  usePermDeleteCompanyMutation,
  useRestoreCompanyMutation,
} from '../../store/api/adminApi'
import {
  dayjsFormat,
  formatPhoneNumber,
  formatPlanName,
  formatTimestamp,
  getInitials,
  notify,
} from '../../shared/utilities/util'
import { getCompanyStatusConfig } from '../../shared/utilities/statusConfig'

const CustomTooltip = ({ active, payload, label, prefix = '' }: any) => {
  if (!active || !payload?.length) return null

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '10px',
        px: 1.5,
        py: 1,
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        minWidth: 140,
      }}
    >
      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>{label}</Typography>
      {payload.map((entry: any, i: number) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: entry.color }} />
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{entry.name}</Typography>
          </Box>
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.primary' }}>
            {prefix}
            {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

const CustomLegend = ({ payload }: any) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      gap: 2,
      flexWrap: 'wrap',
      mt: 2,
      pt: 1.5,
      borderTop: '1px solid',
      borderColor: 'divider',
    }}
  >
    {payload?.map((entry: any, i: number) => (
      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 10, height: 3, borderRadius: '2px', bgcolor: entry.color }} />
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.primary' }}>{entry.value}</Typography>
      </Box>
    ))}
  </Box>
)

interface DetailComponentProps {
  isLoading: boolean
  subscriberData: Company
  companySetting?: any
  analyticsData: any
  onRefetch?: () => void
}

const DetailComponent = ({
  isLoading,
  subscriberData,
  companySetting,
  analyticsData,
  onRefetch,
}: DetailComponentProps) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isDark } = useThemeMode()
  const isSuperAdmin = useIsSuperAdmin()
  const [restoreCompanyMut, { isLoading: loading }] = useRestoreCompanyMutation()
  const [deleteCompanyMut] = useDeleteCompanyMutation()
  const [permDeleteCompanyMut] = usePermDeleteCompanyMutation()
  const [deleteLoading, setDeleteLoading] = useState(false)
  // Deep-link support: `?tab=ai-usage` (e.g. from the AI Spend fleet page) opens the AI Usage tab.
  const location = useLocation()
  const [activeTab, setActiveTab] = useState(new URLSearchParams(location.search).get('tab') === 'ai-usage' ? 2 : 0)
  // Log in as is open to every active admin (owner Q4), so it sits outside the SuperAdmin gate below.
  // Hidden while the members probe runs and when it returns 404 ADMIN_IMPERSONATION_DISABLED (flag OFF).
  // Any other failure shows both buttons; the dialog surfaces the error.
  const [canLogInAs, setCanLogInAs] = useState(false)
  const [logInAsOpen, setLogInAsOpen] = useState(false)
  const isDeleted = !!subscriberData?.deleted

  useEffect(() => {
    setCanLogInAs(false)
    if (!id || isLoading || isDeleted) return
    let live = true
    void (async () => {
      try {
        await getImpersonationMembers(id, { limit: 1 })
        if (live) setCanLogInAs(true)
      } catch (err) {
        if (live) setCanLogInAs(logInAsVisibleAfterProbe(err))
      }
    })()
    return () => {
      live = false
    }
  }, [id, isLoading, isDeleted])

  const handleRestore = async () => {
    try {
      await restoreCompanyMut(subscriberData?._id).unwrap()
      notify('Subscriber restored successfully', 'success')
      onRefetch?.()
    } catch (error) {}
  }

  const handleDelete = async () => {
    try {
      setDeleteLoading(true)
      await deleteCompanyMut(subscriberData?._id).unwrap()
      notify('Subscriber deleted successfully', 'success')
      onRefetch?.()
    } catch (error) {
    } finally {
      setDeleteLoading(false)
    }
  }

  const handlePermDelete = async () => {
    try {
      setDeleteLoading(true)
      await permDeleteCompanyMut([subscriberData?._id]).unwrap()
      notify('Subscriber permanently deleted', 'success')
      navigate(subscribersPath)
    } catch (error) {
    } finally {
      setDeleteLoading(false)
    }
  }

  const formatDate = (date: string) => {
    if (!date) return '--'
    const d = new Date(date)
    const day = d.getDate()
    const month = d.toLocaleString('en-US', { month: 'short' }).toLowerCase()
    const year = d.getFullYear()
    return `${day} ${month} ${year}`
  }

  const getOwnerName = () => {
    const owner = subscriberData?.owner
    if (!owner) return 'N/A'
    const name = [owner.firstName, owner.lastName].filter(Boolean).join(' ')
    return name || 'N/A'
  }

  const iconSx = { fontSize: 16, color: 'text.secondary' }

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '--'
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
  }

  return (
    <Box>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
          borderRadius: '12px',
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, minWidth: 0, flex: 1 }}>
          <Button
            onClick={() => navigate(subscribersPath)}
            sx={{
              minWidth: 36,
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: '10px',
              color: 'text.secondary',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
              '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
            }}
            aria-label="Back to subscribers"
          >
            <ArrowBackOutlinedIcon sx={{ fontSize: 20 }} />
          </Button>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ fontSize: { xs: '18px', sm: '22px' }, fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}
            >
              {isLoading ? <Skeleton width={200} /> : subscriberData?.companyName || 'N/A'}
            </Typography>
            <Typography noWrap sx={{ fontSize: '13px', color: 'text.secondary', mt: 0.25 }}>
              Subscriber details
            </Typography>
          </Box>
        </Box>

        {/*
          The whole subscriber record stays readable for a plain Admin — that is
          the support-desk view. Only the three lifecycle writes are hidden:
          soft delete (DELETE /admin/company/:id), permanent delete
          (DELETE /admin/company-permanently) and restore
          (PATCH /admin/restore-company/:id) are SuperAdmin-only server-side, so
          for anyone else these buttons fire straight into a 400 — there is no
          confirm dialog on this page to catch it. The deleted banner below is
          unaffected: a plain Admin still sees THAT the company is deleted, they
          just cannot undo it.
        */}
        {!isLoading && (canLogInAs || isSuperAdmin) && (
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
            {canLogInAs && id && (
              <>
                <SharedButton
                  label="Support sessions"
                  btnVariant="text"
                  onClick={() => navigate(`${impersonationAuditPath}?companyId=${encodeURIComponent(id)}`)}
                  startIcon={<HistoryOutlinedIcon />}
                />
                <SharedButton
                  label="Log in as"
                  btnVariant="secondary"
                  onClick={() => setLogInAsOpen(true)}
                  startIcon={<LoginOutlinedIcon />}
                />
              </>
            )}
            {isSuperAdmin &&
              (subscriberData?.deleted ? (
                <>
                  <SharedButton
                    label="Restore"
                    btnVariant="secondary"
                    onClick={handleRestore}
                    loading={loading}
                    startIcon={<RestoreFromTrashOutlinedIcon />}
                  />
                  <SharedButton
                    label="Delete"
                    btnVariant="danger"
                    onClick={handlePermDelete}
                    loading={deleteLoading}
                    startIcon={<DeleteForeverOutlinedIcon />}
                  />
                </>
              ) : (
                <SharedButton
                  label="Delete"
                  btnVariant="danger-outline"
                  onClick={handleDelete}
                  loading={deleteLoading}
                  startIcon={<DeleteOutlinedIcon />}
                />
              ))}
          </Box>
        )}
      </Paper>

      {/* Deleted Banner */}
      {!isLoading && subscriberData?.deleted && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: '10px',
            bgcolor: isDark ? 'rgba(239, 68, 68, 0.1)' : '#fef2f2',
            border: `1px solid ${isDark ? 'rgba(239, 68, 68, 0.2)' : '#fecaca'}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <DeleteForeverOutlinedIcon sx={{ fontSize: 20, color: 'error.main', flexShrink: 0 }} />
          <Typography sx={{ fontSize: { xs: '12px', sm: '13px' }, fontWeight: 600, color: 'error.main' }}>
            This subscriber has been deleted.
          </Typography>
        </Box>
      )}

      {/* Stats Row */}
      {!isLoading && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <StatCard label="Plan" value={formatPlanName(subscriberData?.planType)} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard label="Team Members" value={subscriberData?.memberCount ?? 0} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard label="Status" value={getCompanyStatusConfig(subscriberData).label} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard label="Interval" value={subscriberData?.interval || '--'} />
          </Grid>
        </Grid>
      )}

      {isLoading && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[...Array(4)].map((_, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                  borderRadius: '12px',
                  p: 2.5,
                }}
              >
                <Skeleton width={60} height={14} sx={{ mb: 1 }} />
                <Skeleton width={80} height={28} />
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Content */}
      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
              borderRadius: '12px',
              p: { xs: 2.5, sm: 3 },
              textAlign: 'center',
              height: 'max-content',
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <Skeleton variant="rectangular" width={88} height={88} sx={{ borderRadius: '18px' }} />
                <Skeleton width={120} height={24} />
                <Skeleton width={80} height={24} />
                <Divider sx={{ width: '100%', my: 1 }} />
                <Skeleton width="80%" height={20} />
                <Skeleton width="60%" height={20} />
                <Skeleton width="70%" height={20} />
              </Box>
            ) : (
              <>
                <Avatar
                  src={subscriberData?.imageUrl}
                  alt={subscriberData?.companyName || 'Company'}
                  sx={{
                    width: { xs: 64, sm: 88 },
                    height: { xs: 64, sm: 88 },
                    mx: 'auto',
                    mb: 2,
                    p: 0.5,
                    fontSize: { xs: 24, sm: 30 },
                    fontWeight: 700,
                    borderRadius: { xs: '14px', sm: '18px' },
                    border: `2px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
                    '& .MuiAvatar-img': { objectFit: 'contain' },
                    bgcolor: isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(37, 99, 235, 0.08)',
                    color: isDark ? '#60a5fa' : '#2563eb',
                  }}
                  variant="rounded"
                >
                  {getInitials(subscriberData?.companyName)}
                </Avatar>
                <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'text.primary', mb: 1 }}>
                  {subscriberData?.companyName || 'N/A'}
                </Typography>
                {(() => {
                  const sc = getCompanyStatusConfig(subscriberData)
                  return (
                    <Chip
                      label={sc.label}
                      size="small"
                      icon={
                        <Box
                          sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: isDark ? sc.darkColor : sc.color }}
                        />
                      }
                      sx={{
                        fontWeight: 600,
                        fontSize: '12px',
                        borderRadius: '6px',
                        color: isDark ? sc.darkColor : sc.color,
                        bgcolor: isDark ? sc.darkBg : sc.bg,
                        '& .MuiChip-icon': { ml: 0.5 },
                      }}
                    />
                  )
                })()}
                <Divider sx={{ my: 2.5, borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9' }} />
                <Box sx={{ textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <PersonOutlinedIcon sx={iconSx} />
                    <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{getOwnerName()}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <EmailOutlinedIcon sx={iconSx} />
                    <Typography sx={{ fontSize: '13px', color: 'text.secondary', wordBreak: 'break-all' }}>
                      {subscriberData?.owner?.email || 'N/A'}
                    </Typography>
                  </Box>
                  {subscriberData?.owner?.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <PhoneOutlinedIcon sx={iconSx} />
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                        {formatPhoneNumber(subscriberData.owner.phone, subscriberData.owner.phone)}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CalendarTodayOutlinedIcon sx={iconSx} />
                    <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                      Joined {formatDate(subscriberData?.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Paper>
        </Grid>

        {/* Details Cards */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {/* Subscription Info */}
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                  borderRadius: '12px',
                  p: { xs: 2.5, sm: 3 },
                  height: '100%',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    mb: 2,
                  }}
                >
                  Subscription
                </Typography>
                {isLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[...Array(4)].map((_, i) => (
                      <Skeleton key={i} height={20} />
                    ))}
                  </Box>
                ) : (
                  <>
                    <InfoRow
                      icon={<CreditCardOutlinedIcon sx={iconSx} />}
                      label="Plan"
                      value={formatPlanName(subscriberData?.planType)}
                    />
                    <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' }} />
                    <InfoRow
                      icon={<FiberManualRecordOutlinedIcon sx={iconSx} />}
                      label="Status"
                      value={(() => {
                        const sc = getCompanyStatusConfig(subscriberData)
                        return (
                          <Chip
                            label={sc.label}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '11px',
                              height: 22,
                              borderRadius: '6px',
                              color: isDark ? sc.darkColor : sc.color,
                              bgcolor: isDark ? sc.darkBg : sc.bg,
                            }}
                          />
                        )
                      })()}
                    />
                    <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' }} />
                    <InfoRow
                      icon={<GroupsOutlinedIcon sx={iconSx} />}
                      label="Subscribed Team Size"
                      value={subscriberData?.subscribedTeamSize}
                    />
                    <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' }} />
                    <InfoRow
                      icon={<RepeatOutlinedIcon sx={iconSx} />}
                      label="Subscription Interval"
                      value={subscriberData?.interval}
                    />
                    <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' }} />
                    <InfoRow
                      icon={<PaidOutlinedIcon sx={iconSx} />}
                      label="Amount Paid"
                      value={formatAmount(subscriberData?.amountPaid)}
                    />
                    <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' }} />
                    <InfoRow
                      icon={<EventAvailableOutlinedIcon sx={iconSx} />}
                      label="Start Date"
                      value={
                        subscriberData?.subscriptionStartDate
                          ? formatTimestamp(subscriberData.subscriptionStartDate)
                          : '--'
                      }
                    />
                    <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' }} />
                    <InfoRow
                      icon={<EventBusyOutlinedIcon sx={iconSx} />}
                      label="End Date"
                      value={
                        subscriberData?.subscriptionEndDate ? formatTimestamp(subscriberData.subscriptionEndDate) : '--'
                      }
                    />
                    <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' }} />
                    <InfoRow
                      icon={<AutorenewOutlinedIcon sx={iconSx} />}
                      label="Next Billing"
                      value={subscriberData?.nextCycle ? formatTimestamp(subscriberData.nextCycle) : '--'}
                    />
                    {subscriberData?.cancelAt && (
                      <>
                        <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' }} />
                        <InfoRow
                          icon={<CancelScheduleSendOutlinedIcon sx={iconSx} />}
                          label="Cancels On"
                          value={formatTimestamp(subscriberData.cancelAt)}
                        />
                      </>
                    )}
                    {subscriberData?.isUpgraded && (
                      <>
                        <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' }} />
                        <InfoRow
                          icon={<UpgradeOutlinedIcon sx={iconSx} />}
                          label="Upgraded"
                          value={
                            <Chip
                              label="Yes"
                              color="info"
                              size="small"
                              sx={{ fontWeight: 600, fontSize: '11px', height: 22, borderRadius: '6px' }}
                            />
                          }
                        />
                      </>
                    )}
                  </>
                )}
              </Paper>
            </Grid>

            {/* Account Info */}
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                  borderRadius: '12px',
                  p: { xs: 2.5, sm: 3 },
                  height: 'max-content',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    mb: 2,
                  }}
                >
                  Company Details
                </Typography>
                {isLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[...Array(4)].map((_, i) => (
                      <Skeleton key={i} height={20} />
                    ))}
                  </Box>
                ) : (
                  <>
                    <InfoRow
                      icon={<WorkOutlineOutlinedIcon sx={iconSx} />}
                      label="Work Type"
                      value={subscriberData?.workType}
                    />
                    <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' }} />
                    <InfoRow
                      icon={<GroupsOutlinedIcon sx={iconSx} />}
                      label="Company Team Size"
                      value={subscriberData?.teamSize}
                    />
                    <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' }} />
                    <InfoRow
                      icon={<WorkOutlineOutlinedIcon sx={iconSx} />}
                      label="Founded"
                      value={subscriberData?.foundingYear}
                    />
                    <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' }} />
                    <InfoRow
                      icon={<CalendarTodayOutlinedIcon sx={iconSx} />}
                      label="Created"
                      value={formatDate(subscriberData?.createdAt)}
                    />
                  </>
                )}
              </Paper>
            </Grid>

            {/*
              SuperAdmin only: both tradesman-switch routes refuse a plain Admin (nhrapp#4397), same rule as
              the E-signature tab. A deleted company answers 404, so the card is left out for one.
            */}
            {!isLoading && isSuperAdmin && id && !subscriberData?.deleted && (
              <Grid item xs={12}>
                <TradesmanSwitchCard companyId={id} companyName={subscriberData?.companyName ?? ''} />
              </Grid>
            )}

            {/* SuperAdmin only: PATCH /admin/company/:id/metrics-exclusion refuses a plain Admin. Deleted companies are out of metrics anyway. */}
            {!isLoading && isSuperAdmin && id && !subscriberData?.deleted && (
              <Grid item xs={12}>
                <MetricsExclusionCard companyId={id} excluded={subscriberData?.excludeFromMetrics === true} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Box sx={{ mb: 2 }}>
          {/*
            E-signature is appended LAST so the existing indices (and the `?tab=ai-usage`
            deep link to index 2) do not move, and only for a SuperAdmin: both routes behind
            it are SuperAdmin-only server-side, and the provider switch is a platform-operator
            control that a company's own staff never see.
          */}
          <SharedTabs
            tabs={
              isSuperAdmin
                ? ['User History', 'Invoices', 'AI Usage', 'E-signature']
                : ['User History', 'Invoices', 'AI Usage']
            }
            value={activeTab}
            onChange={setActiveTab}
            ariaLabel="Subscriber detail tabs"
          />
        </Box>
        {activeTab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Summary Stats */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, auto)' },
                gap: { xs: 1.5, sm: 3 },
                alignItems: 'center',
              }}
            >
              {[
                {
                  label: 'Total Payment',
                  value: `$${(analyticsData?.totalPayment ?? 0) / 100}`,
                },
                {
                  label: 'Total Credits',
                  value: analyticsData?.totalCredits ?? 0,
                },
                {
                  label: 'Team Size',
                  value: analyticsData?.totalTeamSize ?? 0,
                },
                {
                  label: 'Total Projects',
                  value: analyticsData?.totalProjects ?? 0,
                },
              ].map((stat, i) => (
                <Typography key={i} sx={{ fontSize: '13px', color: 'text.secondary' }}>
                  {stat.label}:{' '}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontSize: '14px',
                    }}
                  >
                    {stat.value}
                  </Box>
                </Typography>
              ))}
            </Box>

            {/* Team History Chart */}
            {analyticsData?.teamHistory?.length > 0 && (
              <Box
                sx={{
                  p: 2.5,
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                  borderRadius: '12px',
                  bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fff',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    mb: 2,
                  }}
                >
                  Team History
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart
                    data={analyticsData.teamHistory.map((e: any) => ({
                      ...e,
                      date: dayjsFormat(e.date, 'MMM DD, YYYY'),
                    }))}
                    margin={{ top: 5, right: 10, bottom: 30, left: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{
                        fontSize: 11,
                        fill: isDark ? '#64748b' : '#94a3b8',
                      }}
                      axisLine={false}
                      tickLine={false}
                      interval={Math.max(0, Math.floor(analyticsData.teamHistory.length / 8))}
                      label={{
                        value: 'Date',
                        position: 'insideBottom',
                        offset: -12,
                        fontSize: 11,
                        fill: isDark ? '#64748b' : '#94a3b8',
                        fontWeight: 600,
                      }}
                    />
                    <YAxis
                      tick={{
                        fontSize: 11,
                        fill: isDark ? '#64748b' : '#94a3b8',
                      }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                      width={35}
                      label={{
                        value: 'Members',
                        angle: -90,
                        position: 'insideLeft',
                        offset: 0,
                        fontSize: 11,
                        fill: isDark ? '#64748b' : '#94a3b8',
                        fontWeight: 600,
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend content={<CustomLegend />} />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name="Count"
                      stroke={isDark ? '#e2e8f0' : '#1e293b'}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="invited"
                      name="Invited"
                      stroke={isDark ? '#34d399' : '#16a34a'}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="joined"
                      name="Joined"
                      stroke={isDark ? '#60a5fa' : '#2563eb'}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="terminated"
                      name="Terminated"
                      stroke={isDark ? '#f87171' : '#dc2626'}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="restored"
                      name="Restored"
                      stroke={isDark ? '#a78bfa' : '#7c3aed'}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}

            {!analyticsData?.teamHistory?.length && !analyticsData?.projectsHistory?.length && (
              <Box
                sx={{
                  py: 4,
                  textAlign: 'center',
                  color: 'text.secondary',
                  fontSize: '14px',
                }}
              >
                No user history available
              </Box>
            )}
          </Box>
        )}
        {activeTab === 1 && <Invoice id={id} />}
        {activeTab === 2 && <AiUsage id={id} />}
        {activeTab === 3 && isSuperAdmin && <ESignature id={id} companyName={subscriberData?.companyName ?? ''} />}
      </Box>

      {/* Company Settings (full width) */}
      <Box sx={{ mt: 3 }}>
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
            borderRadius: '12px',
            p: { xs: 2.5, sm: 3 },
            height: 'max-content',
          }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              mb: 2,
            }}
          >
            Company Settings
          </Typography>
          {isLoading ? (
            <Grid container spacing={2}>
              {[...Array(9)].map((_, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton height={20} />
                </Grid>
              ))}
            </Grid>
          ) : (
            (() => {
              const cs: any = companySetting
              if (!cs) {
                return (
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    No company settings available.
                  </Typography>
                )
              }
              const fmtNum = (v: any) => (v == null || v === '' ? '--' : String(v))
              const fmtArr = (v: any) => (Array.isArray(v) && v.length ? v.join(', ') : '--')
              const groups: { title: string; rows: { label: string; value: any }[] }[] = [
                {
                  title: 'General',
                  rows: [
                    { label: 'Time Zone', value: fmtNum(cs.timeZone) },
                    { label: 'Default PO', value: fmtNum(cs.defaultPO) },
                    { label: 'Payment Processor', value: fmtNum(cs.defaultPaymentProcessor) },
                    { label: 'GPS Enabled', value: fmtNum(cs.gpsEnable) },
                    { label: 'Working States', value: fmtArr(cs.workingStates) },
                    { label: 'Address', value: fmtNum(cs.address) },
                  ],
                },
              ]

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {groups.map((g) => (
                    <Box key={g.title}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                          px: 1.5,
                          py: 0.75,
                          borderRadius: '8px',
                          bgcolor: isDark ? 'rgba(59, 130, 246, 0.12)' : 'rgba(37, 99, 235, 0.08)',
                          borderLeft: `3px solid ${isDark ? '#60a5fa' : '#2563eb'}`,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '13px',
                            fontWeight: 700,
                            color: isDark ? '#60a5fa' : '#2563eb',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            lineHeight: 1,
                          }}
                        >
                          {g.title}
                        </Typography>
                      </Box>
                      <Grid container columnSpacing={3}>
                        {g.rows.map((r) => (
                          <Grid item xs={12} sm={6} md={4} key={r.label}>
                            <InfoRow label={r.label} value={r.value} />
                            <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc' }} />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ))}
                </Box>
              )
            })()
          )}
        </Paper>
      </Box>
      {canLogInAs && id && (
        <LogInAsDialog
          open={logInAsOpen}
          onClose={() => setLogInAsOpen(false)}
          companyId={id}
          companyName={subscriberData?.companyName ?? ''}
        />
      )}
    </Box>
  )
}

export default DetailComponent
